import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { TextArea } from '../../ui/TextArea';
import { Alert } from '../../ui/Alert';
import { PeoplePicker } from './PeoplePicker';
import { AttachmentPicker } from './AttachmentPicker';
import { OriginPicker } from './OriginPicker';
import { toLocalInput, fromLocalInput } from '../../../utils/momentDates';

export const MomentFormModal = ({ moment, onSave, onClose, progress, defaultPeople = [] }) => {
  const startPeople = moment ? (moment.people || []) : defaultPeople;
  const [values, setValues] = useState({
    title: moment?.title || '',
    body: moment?.body || '',
    when: toLocalInput(moment?.occurred_at)
  });
  const [originId, setOriginId] = useState(moment?.origin_id || null);
  const [originNote, setOriginNote] = useState(moment?.origin_note || '');
  const [contactIds, setContactIds] = useState(startPeople.map((p) => p.id));
  const [pending, setPending] = useState([]);
  const [removedIds, setRemovedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const pendingRef = useRef([]);
  pendingRef.current = pending;

  useEffect(() => () => pendingRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl)), []);

  const kept = (moment?.attachments || []).filter((a) => !removedIds.includes(a.id));
  const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const dropPending = (key) => {
    const item = pending.find((p) => p.key === key);
    if (item) URL.revokeObjectURL(item.previewUrl);
    setPending((list) => list.filter((p) => p.key !== key));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!values.body.trim() && kept.length + pending.length === 0) {
      setError('Add a note, a photo, a file or a recording.');
      return;
    }
    if (!values.when || Number.isNaN(new Date(values.when).getTime())) {
      setError('Please choose when this happened.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        title: values.title,
        body: values.body,
        occurred_at: fromLocalInput(values.when),
        origin_id: originId,
        origin_note: originNote,
        contactIds,
        newItems: pending,
        removeIds: removedIds
      });
    } catch (err) {
      setError(err.message || 'Could not save this moment.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 p-0 sm:p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-6 space-y-4 max-h-[92vh] overflow-y-auto shadow-xl"
      >
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">
          {moment ? 'Edit moment' : 'Capture a moment'}
        </h2>
        {error && <Alert variant="error" message={error} />}
        <TextArea
          label="What happened?"
          placeholder="A conversation, a feeling, something worth remembering..."
          value={values.body}
          onChange={set('body')}
          rows={6}
          maxLength={20000}
          autoFocus
        />
        <AttachmentPicker
          existing={kept}
          pending={pending}
          disabled={saving}
          onAdd={(items) => setPending((list) => [...list, ...items])}
          onRemovePending={dropPending}
          onRemoveExisting={(id) => setRemovedIds((ids) => [...ids, id])}
        />
        <PeoplePicker selectedIds={contactIds} initialPeople={startPeople} onChange={setContactIds} />
        <OriginPicker value={originId} note={originNote} onChange={setOriginId} onNoteChange={setOriginNote} />
        <Input label="Title (optional)" placeholder="Give it a name" value={values.title} onChange={set('title')} maxLength={200} />
        <Input label="When did it happen?" type="datetime-local" value={values.when} onChange={set('when')} />
        {progress && <p className="text-sm font-medium text-slate-500">{progress}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" isLoading={saving}>{moment ? 'Save changes' : 'Save moment'}</Button>
        </div>
      </form>
    </div>
  );
};